import { useRef, useState, useEffect } from 'react';
import { Volume2, VolumeX, Maximize2, Play } from 'lucide-react';
import useIsMobile from '@/hooks/useIsMobile';
import { useLanguage } from '@/lib/LanguageContext';

/**
 * Reproductor de video MP4 reutilizable.
 * - Formato 16:9 (aspect-video)
 * - Autoplay silenciado (requisito del navegador)
 * - Botón de activar/desactivar sonido
 * - Botón de pantalla completa
 */
export default function VideoPlayer({ src, className = '', fit = 'contain', loop = true, preload = 'metadata', lazy = true, poster, fetchPriority = 'auto' }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [shouldLoad, setShouldLoad] = useState(!lazy);
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  const [userStarted, setUserStarted] = useState(false);
  const ready = shouldLoad && (!isMobile || userStarted);

  useEffect(() => {
    if (ready && videoRef.current) videoRef.current.muted = muted;
  }, [muted, ready]);

  // En móvil, tras tocar play, inicia la reproducción
  useEffect(() => {
    if (userStarted && videoRef.current && ready) {
      videoRef.current.play().catch(() => {});
    }
  }, [userStarted, ready]);

  // Lazy-load: no descarga nada hasta que el video está cerca del viewport
  useEffect(() => {
    if (shouldLoad) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoad]);

  const handleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    }
  };

  return (
    <div ref={containerRef} className={`relative overflow-hidden aspect-video bg-white ${className}`}>
      <video
        ref={videoRef}
        src={ready ? src : undefined}
        autoPlay={!isMobile}
        loop={loop}
        muted={muted}
        playsInline
        preload={ready ? preload : 'none'}
        poster={poster}
        fetchPriority={fetchPriority}
        className={`w-full h-full ${fit === 'contain' ? 'object-contain' : 'object-cover'}`}
      />

      {/* Móvil: tocar para reproducir (evita descargar el video al hacer scroll) */}
      {isMobile && !userStarted && (
        <button
          onClick={() => setUserStarted(true)}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/25"
          aria-label={t('aria.playVideo')}
        >
          <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
            <Play size={26} className="text-brand-blue ml-1" fill="currentColor" />
          </span>
        </button>
      )}
      <div className="absolute top-4 left-4 z-10 pointer-events-none">
        <img
          src="https://media.base44.com/images/public/6a877e598e2aae01ac439787/966e8fe32_Logo6.png/v1/fit/w_200,h_64,q_90,usm_0.66_1.00_0.01,enc_webp,quality_auto/Logo6.webp"
          alt="SmartPOS"
          className="h-8 w-auto drop-shadow-lg"
          draggable={false}
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="absolute bottom-4 right-4 flex gap-2 z-10">
        <button
          onClick={() => setMuted((m) => !m)}
          className="w-10 h-10 rounded-full glassmorphism flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label={muted ? t('aria.mute') : t('aria.unmute')}
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        <button
          onClick={handleFullscreen}
          className="w-10 h-10 rounded-full glassmorphism flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label={t('aria.fullscreen')}
        >
          <Maximize2 size={18} />
        </button>
      </div>
    </div>
  );
}